import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';

interface AssignSharedDataPoolProps {
  cpeId: string;
  onSubmit: () => void;
}

export const AssignSharedDataPool = ({ cpeId, onSubmit }: AssignSharedDataPoolProps) => {
  const api = useApi();
  const [selectedPoolId, setSelectedPoolId] = useState('');

  const { isLoading, data: dataPools } = useQuery(
    ['dataPools'],
    api.getDataPoolApiV1GetDataPoolGet.bind(null, {}),
    { enabled: true },
  );

  const { isLoading: loadingCpes, data: cpeData } = useQuery(
    ['cellularRouters'],
    api.getCpeCellularApiV1GetCpeCellularGet.bind(null, {}),
    { enabled: true },
  );

  const currentPoolId = useMemo(() => {
    const cpe = cpeData?.data.find((item) => item.cpe_id === cpeId);
    return cpe?.data_pool_id ?? '';
  }, [cpeData, cpeId]);

  const assignDataPool = useMutation(api.addCpeToDataPoolApiV1AddCpeToDataPoolPost, {
    onSuccess() {
      toast.success('Assigned Shared Data Pool successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  useEffect(() => {
    // Wait for the CPE's current data pool assignment to be known before
    // falling back to the first pool, otherwise a slower cellularRouters
    // fetch (vs. the dataPools fetch) can lock in the wrong default.
    if (selectedPoolId || loadingCpes) return;
    if (currentPoolId) {
      setSelectedPoolId(currentPoolId);
    } else if (dataPools?.data.length) {
      setSelectedPoolId(dataPools.data[0].data_pool_id);
    }
  }, [currentPoolId, dataPools, selectedPoolId, loadingCpes]);

  const handleSubmit = () => {
    if (!selectedPoolId) {
      toast.error('Please select a Shared Data Pool.');
      return;
    }
    assignDataPool.mutate({
      cpe_id: cpeId,
      data_pool_id: selectedPoolId,
    });
  };

  if (isLoading || loadingCpes) return <Loading />;

  return (
    <Box pt={2}>
      <FormControl fullWidth>
        <InputLabel id="shared-data-pool-label">Shared Data Pool</InputLabel>
        <Select
          labelId="shared-data-pool-label"
          label="Shared Data Pool"
          value={selectedPoolId}
          onChange={(e) => setSelectedPoolId(e.target.value)}
        >
          {dataPools?.data.map((pool) => (
            <MenuItem value={pool.data_pool_id} key={pool.data_pool_id}>
              {pool.data_pool_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedPoolId || assignDataPool.isLoading}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

AssignSharedDataPool.displayName = 'Assign Shared Data Pool';
